/** @typedef {[number, number]} Point */
/** @typedef {import('./floorPlanDesignerPivotWallPolygons.js').DesignerGraph} DesignerGraph */
/** @typedef {import('./floorPlanDesignerPivotWallPolygons.js').JoinPair} JoinPair */
/** @typedef {import('./floorPlanDesignerState.js').DesignerWall} DesignerWall */

import {
  DEFAULT_WALL_THICKNESS_M,
  getPointById,
} from './floorPlanDesignerState.js';
import {
  buildCenterlineWallPolygon,
  buildPivotAdjacency,
  getCenterlineWallNormal,
  getWallOffsetLineAtPivot,
  intersectOffsetLines,
  isPivotEdgeLongEnough,
  simpleOffsetPoint,
  validatePivotWallPolygon,
} from './floorPlanDesignerPivotWallPolygons.js';
import {
  buildJunctionAwareWallGeometry,
  junctionWeldedEndKey,
} from './floorPlanDesignerJunctionAwareWallGeometry.js';
import {
  isPolygonSelfIntersecting,
  MIN_POLYGON_AREA,
  polygonArea,
} from './floorPlanDesignerWallRectangles.js';
import { pointOnWallAtT } from './floorPlanWallGeometry.js';

const COLLINEAR_DOT_EPS = 0.985;
/** Body end inset disabled — caps share welded vertices with body (no geometric gap) */
export const CAP_BODY_INSET_METERS = 0;
/** Inward depth for cap triangle tips (pixels) — avoids degenerate caps when center lies on thickness edge */
const CAP_FAN_DEPTH_PX = 0.5;

const IS_DEV = process.env.NODE_ENV === 'development';

/**
 * @typedef {{
 *   wallId: string;
 *   otherPointId: string;
 *   angle: number;
 * }} SortedIncident
 */

/**
 * @typedef {{
 *   body: Point[];
 *   startCaps: [Point[], Point[]];
 *   endCaps: [Point[], Point[]];
 * }} WallSplitGeometry
 */

/**
 * @typedef {import('./floorPlanDesignerJunctionAwareWallGeometry.js').WeldedEndSides} WeldedEndSides
 */

/**
 * @typedef {{
 *   wallBodies: Map<string, Point[]>;
 *   startCaps: Map<string, [Point[], Point[]]>;
 *   endCaps: Map<string, [Point[], Point[]]>;
 *   junctionClosurePolygons: Array<{ nodeKey: string; ownerWallId: string; polygon: Point[] }>;
 *   junctionClosurePolygonsByWall: Map<string, Point[][]>;
 *   pivotWelds: Map<string, WeldedEndSides>;
 *   wallPivotJoins: Map<string, JoinPair>;
 *   joinPoints: Map<string, JoinPair | null>;
 *   bevelPivots: Set<string>;
 * }} DynamicWallRenderModel
 */

/**
 * @param {Point[]} polygon
 * @param {string} [label]
 * @returns {Point[] | null}
 */
export function validateDerivedPolygon(polygon, label = '') {
  if (!polygon || polygon.length < 3) return null;
  if (!polygon.every((p) => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))) {
    if (IS_DEV) {
      console.warn('[2D Designer] skipped invalid derived polygon', { label, reason: 'non-finite' });
    }
    return null;
  }
  if (Math.abs(polygonArea(polygon)) < MIN_POLYGON_AREA) return null;
  if (polygon.length >= 4 && isPolygonSelfIntersecting(polygon)) {
    if (IS_DEV) {
      console.warn('[2D Designer] skipped invalid derived polygon', { label, reason: 'self-intersect' });
    }
    return null;
  }
  return polygon;
}

/**
 * @param {string} pivotId
 * @param {string} wallId
 * @returns {string}
 */
export function dynamicWallPivotJoinKey(pivotId, wallId) {
  return `${pivotId}|${wallId}`;
}

/**
 * @param {SortedIncident} a
 * @param {SortedIncident} b
 * @returns {boolean}
 */
function areIncidentsCollinear(a, b) {
  const dot = Math.cos(a.angle) * Math.cos(b.angle) + Math.sin(a.angle) * Math.sin(b.angle);
  return Math.abs(dot) >= COLLINEAR_DOT_EPS;
}

/**
 * @typedef {{ items: SortedIncident[]; representative: SortedIncident; angle: number }} IncidentGroup
 */

/**
 * @param {SortedIncident[]} sorted
 * @returns {IncidentGroup[]}
 */
export function groupCollinearIncidents(sorted) {
  if (sorted.length === 0) return [];
  /** @type {IncidentGroup[]} */
  const groups = [];

  for (const inc of sorted) {
    const last = groups[groups.length - 1];
    if (last && areIncidentsCollinear(last.representative, inc)) {
      last.items.push(inc);
    } else {
      groups.push({ items: [inc], representative: inc, angle: inc.angle });
    }
  }

  if (groups.length > 1) {
    const first = groups[0];
    const last = groups[groups.length - 1];
    if (areIncidentsCollinear(first.representative, last.representative)) {
      first.items.push(...last.items);
      groups.pop();
    }
  }

  return groups;
}

/**
 * @param {Array<{ wallId: string; otherPointId: string }>} incidents
 * @param {DesignerGraph} graph
 * @param {string} pivotId
 * @returns {SortedIncident[]}
 */
export function sortIncidentsAtPivot(incidents, graph, pivotId) {
  const pivotPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), pivotId);
  if (!pivotPt) return [];

  const pivot = [pivotPt.x, pivotPt.y];
  /** @type {SortedIncident[]} */
  const sorted = [];

  for (const inc of incidents) {
    const otherPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), inc.otherPointId);
    if (!otherPt) continue;
    const dx = otherPt.x - pivot[0];
    const dy = otherPt.y - pivot[1];
    if (Math.hypot(dx, dy) < 1e-6) continue;
    sorted.push({
      wallId: inc.wallId,
      otherPointId: inc.otherPointId,
      angle: Math.atan2(dy, dx),
    });
  }

  sorted.sort((a, b) => {
    if (a.angle !== b.angle) return a.angle - b.angle;
    return a.wallId.localeCompare(b.wallId);
  });

  return sorted;
}

/**
 * @param {Point} pivot
 * @param {Point} join
 * @param {number} maxJoinDistance
 * @returns {boolean}
 */
function isJoinDistanceValid(pivot, join, maxJoinDistance) {
  return Math.hypot(join[0] - pivot[0], join[1] - pivot[1]) <= maxJoinDistance;
}

/**
 * @param {DesignerGraph} graph
 * @param {Point} pivot
 * @param {string} pivotId
 * @param {DesignerWall} wallA
 * @param {DesignerWall} wallB
 * @param {number} pixelsPerMeter
 * @returns {JoinPair | null}
 */
export function computeGraphJoinPairAtPivot(
  graph,
  pivot,
  pivotId,
  wallA,
  wallB,
  pixelsPerMeter,
) {
  /** @type {JoinPair | null} */
  let joinPair = null;

  for (const sideSign of /** @type {const} */ ([1, -1])) {
    const lineA = getWallOffsetLineAtPivot(pivot, pivotId, wallA, graph, pixelsPerMeter, sideSign);
    const lineB = getWallOffsetLineAtPivot(pivot, pivotId, wallB, graph, pixelsPerMeter, sideSign);
    if (!lineA || !lineB) return null;

    const maxJoinDistance = Math.max(lineA.halfThicknessPx * 8, 20);
    const join = intersectOffsetLines(lineA.anchor, lineA.dir, lineB.anchor, lineB.dir);
    if (!join || !Number.isFinite(join[0]) || !Number.isFinite(join[1])) return null;
    if (!isJoinDistanceValid(pivot, join, maxJoinDistance)) return null;

    if (!joinPair) joinPair = { outer: join, inner: join };
    if (sideSign === 1) joinPair.outer = join;
    else joinPair.inner = join;
  }

  return joinPair;
}

/**
 * Two fan triangles at a wall end, apex at centerline node, tips slightly along wall interior.
 * @param {Point} center
 * @param {Point} left
 * @param {Point} right
 * @param {Point} towardInterior
 * @returns {Point[]}
 */
export function buildBisectedEndCaps(center, left, right, towardInterior) {
  const dx = towardInterior[0] - center[0];
  const dy = towardInterior[1] - center[1];
  const len = Math.hypot(dx, dy);
  const depth = len > 1e-6 ? Math.min(Math.max(CAP_FAN_DEPTH_PX, len * 0.02), len * 0.25) : CAP_FAN_DEPTH_PX;
  const tip =
    len > 1e-6
      ? [center[0] + (dx / len) * depth, center[1] + (dy / len) * depth]
      : [center[0] + CAP_FAN_DEPTH_PX, center[1]];

  /** @type {Point[]} */
  const caps = [];
  const leftTri = validateDerivedPolygon([left, center, tip], 'cap-left');
  const rightTri = validateDerivedPolygon([right, center, tip], 'cap-right');
  if (leftTri) caps.push(leftTri);
  if (rightTri) caps.push(rightTri);
  return caps;
}

/**
 * @param {Point} A
 * @param {Point} B
 * @param {Point} A_left
 * @param {Point} A_right
 * @param {Point} B_left
 * @param {Point} B_right
 * @param {number} [insetPx]
 * @returns {WallSplitGeometry | null}
 */
export function buildWallSplitEndGeometryFromCorners(
  A,
  B,
  A_left,
  A_right,
  B_left,
  B_right,
  insetPx = 0,
) {
  let bodyA_left = A_left;
  let bodyA_right = A_right;
  let bodyB_left = B_left;
  let bodyB_right = B_right;

  if (insetPx > 0) {
    const dx = B[0] - A[0];
    const dy = B[1] - A[1];
    const len = Math.hypot(dx, dy);
    if (len > insetPx * 2) {
      const ux = dx / len;
      const uy = dy / len;
      const normal = getCenterlineWallNormal(A, B);
      const halfA = Math.hypot(A_left[0] - A[0], A_left[1] - A[1]);
      const halfB = Math.hypot(B_left[0] - B[0], B_left[1] - B[1]);
      const aOff = [A[0] + ux * insetPx, A[1] + uy * insetPx];
      const bOff = [B[0] - ux * insetPx, B[1] - uy * insetPx];
      bodyA_left = simpleOffsetPoint(aOff, normal, halfA);
      bodyA_right = simpleOffsetPoint(aOff, normal, -halfA);
      bodyB_left = simpleOffsetPoint(bOff, normal, halfB);
      bodyB_right = simpleOffsetPoint(bOff, normal, -halfB);
    }
  }

  const body = buildCenterlineWallPolygon(bodyA_left, bodyB_left, bodyA_right, bodyB_right);
  const startValidated = buildBisectedEndCaps(A, A_left, A_right, B);
  const endValidated = buildBisectedEndCaps(B, B_left, B_right, A);

  if (startValidated.length !== 2 || endValidated.length !== 2) return null;

  return {
    body,
    startCaps: /** @type {[Point[], Point[]]} */ ([startValidated[0], startValidated[1]]),
    endCaps: /** @type {[Point[], Point[]]} */ ([endValidated[0], endValidated[1]]),
  };
}

/**
 * @param {DesignerWall} wall
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @param {Map<string, Array<{ wallId: string; otherPointId: string }>>} adj
 * @param {Map<string, JoinPair | null>} joinPoints
 * @param {Map<string, JoinPair>} wallPivotJoins
 * @param {Set<string>} bevelPivots
 * @param {number} [bodyInsetPx]
 * @returns {WallSplitGeometry | null}
 */
export function buildWallSplitEndGeometry(
  wall,
  graph,
  pixelsPerMeter,
  adj,
  joinPoints,
  wallPivotJoins,
  bevelPivots,
  bodyInsetPx = 0,
) {
  const startPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), wall.startPointId);
  const endPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), wall.endPointId);
  if (!startPt || !endPt) return null;

  const A = [startPt.x, startPt.y];
  const B = [endPt.x, endPt.y];
  if (!isPivotEdgeLongEnough(A, B, pixelsPerMeter)) return null;

  const normal = getCenterlineWallNormal(A, B);
  const thicknessM = wall.thicknessMeters ?? graph.wallSettings.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M;
  const halfThicknessPx = (thicknessM * pixelsPerMeter) / 2;

  const resolveSide = (pivot, pivotId, side) => {
    const sign = side === 'outer' ? 1 : -1;
    const incidents = adj.get(pivotId) ?? [];
    const sorted = sortIncidentsAtPivot(incidents, graph, pivotId);
    const effectiveDegree = groupCollinearIncidents(sorted).length;

    if (effectiveDegree === 2 && !bevelPivots.has(pivotId)) {
      const joinPair =
        wallPivotJoins.get(dynamicWallPivotJoinKey(pivotId, wall.id)) ??
        joinPoints.get(pivotId) ??
        null;
      if (joinPair) {
        return side === 'outer' ? joinPair.outer : joinPair.inner;
      }
    }

    return simpleOffsetPoint(pivot, normal, halfThicknessPx * sign);
  };

  const A_left = resolveSide(A, wall.startPointId, 'outer');
  const A_right = resolveSide(A, wall.startPointId, 'inner');
  const B_left = resolveSide(B, wall.endPointId, 'outer');
  const B_right = resolveSide(B, wall.endPointId, 'inner');

  return buildWallSplitEndGeometryFromCorners(A, B, A_left, A_right, B_left, B_right, bodyInsetPx);
}

/**
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @param {number} [bodyInsetMeters]
 * @returns {DynamicWallRenderModel}
 */
export function buildDynamicWallRenderModel(graph, pixelsPerMeter, bodyInsetMeters = 0) {
  void bodyInsetMeters;
  const junction = buildJunctionAwareWallGeometry({
    points: graph.points,
    walls: graph.walls,
    pixelsPerMeter,
    wallSettings: graph.wallSettings,
  });

  /** @type {Map<string, Point[][]>} */
  const wallBodies = new Map();
  /** @type {Map<string, [Point[], Point[]]>} */
  const startCaps = new Map();
  /** @type {Map<string, [Point[], Point[]]>} */
  const endCaps = new Map();
  /** @type {Map<string, Point[][]>} */
  const junctionClosurePolygonsByWall = new Map();

  for (const [wallId, entry] of junction.walls.entries()) {
    if (entry.bodyPolygon && entry.bodyPolygon.length === 4) {
      wallBodies.set(wallId, entry.bodyPolygon);
    }
  }
  for (const closure of junction.closurePolygons) {
    if (!junctionClosurePolygonsByWall.has(closure.ownerWallId)) {
      junctionClosurePolygonsByWall.set(closure.ownerWallId, []);
    }
    junctionClosurePolygonsByWall.get(closure.ownerWallId).push(closure.polygon);
  }

  return {
    wallBodies,
    startCaps,
    endCaps,
    junctionClosurePolygons: junction.closurePolygons,
    junctionClosurePolygonsByWall,
    pivotWelds: junction.pivotWelds,
    wallPivotJoins: new Map(),
    joinPoints: new Map(),
    bevelPivots: new Set(),
  };
}

/** @alias buildDynamicWallRenderModel */
export const buildSplitWallRenderModel = buildDynamicWallRenderModel;

/**
 * @param {Point} pivot
 * @param {string} pivotId
 * @param {string} wallId
 * @param {{ x: number; y: number }} normal
 * @param {number} halfThicknessPx
 * @param {'outer' | 'inner'} side
 * @param {DynamicWallRenderModel} renderModel
 * @param {Map<string, Array<{ wallId: string; otherPointId: string }>>} adj
 * @param {DesignerGraph} graph
 * @returns {Point}
 */
export function resolveEndpointForSegment(
  pivot,
  pivotId,
  wallId,
  normal,
  halfThicknessPx,
  side,
  renderModel,
  adj,
  graph,
) {
  const sign = side === 'outer' ? 1 : -1;
  const welded = renderModel.pivotWelds?.get(junctionWeldedEndKey(pivotId, wallId));
  if (welded) {
    return side === 'outer' ? welded.outer : welded.inner;
  }

  const incidents = adj.get(pivotId) ?? [];
  const sorted = sortIncidentsAtPivot(incidents, graph, pivotId);
  const effectiveDegree = groupCollinearIncidents(sorted).length;

  if (effectiveDegree === 2 && !renderModel.bevelPivots.has(pivotId)) {
    const joinPair =
      renderModel.wallPivotJoins.get(dynamicWallPivotJoinKey(pivotId, wallId)) ??
      renderModel.joinPoints.get(pivotId) ??
      null;
    if (joinPair) {
      return side === 'outer' ? joinPair.outer : joinPair.inner;
    }
  }

  return simpleOffsetPoint(pivot, normal, halfThicknessPx * sign);
}

/**
 * @typedef {{
 *   body: Point[] | null;
 *   startCaps: [Point[], Point[]] | null;
 *   endCaps: [Point[], Point[]] | null;
 * }} WallSegmentGeometry
 */

/**
 * @param {DesignerGraph} graph
 * @param {string} wallId
 * @param {number} t0
 * @param {number} t1
 * @param {number} pixelsPerMeter
 * @param {DynamicWallRenderModel | null} [renderModel]
 * @param {number} [bodyInsetMeters]
 * @returns {WallSegmentGeometry}
 */
export function buildWallSegmentGeometry(
  graph,
  wallId,
  t0,
  t1,
  pixelsPerMeter,
  renderModel = null,
  bodyInsetMeters = 0,
) {
  void bodyInsetMeters;
  const wall = graph.walls.find((w) => w.id === wallId);
  if (!wall) return { body: null, startCaps: null, endCaps: null };

  const startPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), wall.startPointId);
  const endPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), wall.endPointId);
  if (!startPt || !endPt) return { body: null, startCaps: null, endCaps: null };

  const start = [startPt.x, startPt.y];
  const end = [endPt.x, endPt.y];
  const A = pointOnWallAtT(start, end, t0);
  const B = pointOnWallAtT(start, end, t1);

  if (!isPivotEdgeLongEnough(A, B, pixelsPerMeter)) {
    return { body: null, startCaps: null, endCaps: null };
  }

  const model = renderModel ?? buildDynamicWallRenderModel(graph, pixelsPerMeter, 0);
  const adj = buildPivotAdjacency(graph);
  const normal = getCenterlineWallNormal(start, end);
  const thicknessM = wall.thicknessMeters ?? graph.wallSettings.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M;
  const halfThicknessPx = (thicknessM * pixelsPerMeter) / 2;

  const resolveEndpoint = (pivot, pivotId, t, side) => {
    if (t > 1e-6 && t < 1 - 1e-6) {
      return simpleOffsetPoint(pivot, normal, halfThicknessPx * (side === 'outer' ? 1 : -1));
    }
    return resolveEndpointForSegment(
      pivot,
      pivotId,
      wallId,
      normal,
      halfThicknessPx,
      side,
      model,
      adj,
      graph,
    );
  };

  const A_left = resolveEndpoint(A, wall.startPointId, t0, 'outer');
  const A_right = resolveEndpoint(A, wall.startPointId, t0, 'inner');
  const B_left = resolveEndpoint(B, wall.endPointId, t1, 'outer');
  const B_right = resolveEndpoint(B, wall.endPointId, t1, 'inner');

  const body = validatePivotWallPolygon(
    buildCenterlineWallPolygon(A_left, B_left, A_right, B_right),
    wallId,
  );

  return { body, startCaps: null, endCaps: null };
}

/**
 * @param {DesignerGraph} graph
 * @param {string} wallId
 * @param {number} t0
 * @param {number} t1
 * @param {number} pixelsPerMeter
 * @param {DynamicWallRenderModel | null} [renderModel]
 * @returns {Point[] | null}
 */
export function buildWallBodySegmentPolygon(
  graph,
  wallId,
  t0,
  t1,
  pixelsPerMeter,
  renderModel = null,
) {
  return buildWallSegmentGeometry(graph, wallId, t0, t1, pixelsPerMeter, renderModel).body;
}
